import { c as _c } from "react/compiler-runtime";
// Sessions: where I'm signed in (Devices: this app in a browser, the CLI, a browser session on /auth) and which apps
// hold a grant of mine (Apps: Claude, ChatGPT, … over OAuth); each ends at once. Other instances' grants are on
// their Instances page. A panel (?sessions=1): a page after Settings on desktop, the one Sheet on phones.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type SignIn } from "@/api";
import { useLoad } from "@/lib/load";
import { errorText } from "@/store";
import { ErrorLine } from "./error-line";
import { Group, Stack } from "./group";
import { LinkButton } from "./link-button";
import { SectionHead } from "./section-head";
const day = (iso: string | null) => iso ? iso.slice(0, 16).replace("T", " ") : "\u2014";

/** A user agent → "Firefox · Linux"; the CLI and unknown agents as they are. */
export function device(agent: string | null): string {
  if (!agent) return "\u2014";
  if (!agent.startsWith("Mozilla/")) return agent.split(" ")[0]!;
  const browser = /Headless/.test(agent) ? "Headless" : /Edg\//.test(agent) ? "Edge" : /Firefox\//.test(agent) ? "Firefox" : /Chrome\//.test(agent) ? "Chrome" : /Safari\//.test(agent) ? "Safari" : "Browser";
  const os = /iPhone|iPad/.test(agent) ? "iPhone" : /Android/.test(agent) ? "Android" : /Mac OS X/.test(agent) ? "Mac" : /Windows/.test(agent) ? "Windows" : /Linux/.test(agent) ? "Linux" : "";
  return os ? `${browser} \u00b7 ${os}` : browser;
}
export function Sessions() {
  const $ = _c(40);
  const {
    t
  } = useTranslation();
  const sessions = useLoad(_temp, "sessions");
  const apps = useLoad(_temp2, "apps");
  const [error, setError] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async (fn, ask, reload) => {
      if (!window.confirm(ask)) {
        return;
      }
      setError(null);
      ;
      try {
        await fn();
        reload();
      } catch (t1) {
        const e = t1;
        setError(errorText(e));
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const end = t0;
  let t1;
  if ($[1] !== sessions.reload || $[2] !== t) {
    t1 = s => <Stack key={s.id} data-id={s.id} title={device(s.agent)} value={s.current ? <span className="muted">{t("sessions.current")}</span> : <LinkButton data-t="end-session" onClick={() => void end(() => api.access.endSession(s.id), t("sessions.confirmEnd", {
      device: device(s.agent)
    }), sessions.reload)}>{t("sessions.end")}</LinkButton>} sub={<span><span className="mono">{s.kind}</span> · {t("sessions.used", {
        at: day(s.usedAt ?? s.createdAt)
      })}</span>} />;
    $[1] = sessions.reload;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const row = t1;
  let t2;
  if ($[4] !== sessions.data || $[5] !== sessions.reload || $[6] !== t) {
    t2 = (sessions.data ?? []).some(_temp3) && <LinkButton data-t="end-others" onClick={() => void end(_temp4, t("sessions.confirmOthers"), sessions.reload)}>{t("sessions.endOthers")}</LinkButton>;
    $[4] = sessions.data;
    $[5] = sessions.reload;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = t("sessions.devices");
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <SectionHead action={t2}>{t3}</SectionHead>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = sessions.loading;
  let t6;
  if ($[13] !== sessions.data) {
    t6 = sessions.data ?? [];
    $[13] = sessions.data;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== row || $[16] !== t6) {
    t7 = t6.map(row);
    $[15] = row;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== sessions.loading || $[19] !== t7) {
    t8 = <Group t="devices" busy={t5}>{t7}</Group>;
    $[18] = sessions.loading;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("sessions.apps");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <SectionHead>{t9}</SectionHead>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== apps.data || $[26] !== apps.loading || $[27] !== apps.reload || $[28] !== t) {
    t11 = apps.data?.length === 0 ? <div className="muted note">{t("sessions.noApps")}</div> : <Group t="apps" busy={apps.loading}>{(apps.data ?? []).map(a => <Stack key={a.id} data-id={a.id} title={a.name} value={<LinkButton data-t="revoke-app" onClick={() => void end(() => api.access.revokeApp(a.id), t("sessions.confirmRevoke", {
        app: a.name
      }), apps.reload)}>{t("sessions.revoke")}</LinkButton>} sub={<span><span className="mono">{a.scopes.join(" ")}</span> · {day(a.createdAt)}</span>} />)}</Group>;
    $[25] = apps.data;
    $[26] = apps.loading;
    $[27] = apps.reload;
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== apps.error || $[31] !== error || $[32] !== sessions.error) {
    t12 = (error || sessions.error || apps.error) && <ErrorLine>{error ?? sessions.error ?? apps.error}</ErrorLine>;
    $[30] = apps.error;
    $[31] = error;
    $[32] = sessions.error;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t4 || $[38] !== t8) {
    t13 = <div data-t="sessions">{t4}{t8}{t10}{t11}{t12}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t4;
    $[38] = t8;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}
function _temp4() {
  return api.access.endOthers();
}
function _temp3(s_0) {
  return !s_0.current;
}
function _temp2() {
  return api.access.apps();
}
function _temp() {
  return api.access.sessions();
}